/**
 * Charm Builder — Main controller
 * Initialises on DOMContentLoaded. Reads exclusively from CharmBuilder.state.
 * Listens for 'charm-builder:updated' and calls the relevant render functions.
 *
 * Scripts are loaded with `defer` so DOMContentLoaded may have already fired
 * by the time this runs — use readyState guard instead of a bare listener.
 */

// Bridge: derive the legacy __CHARM_BUILDER_DATA__ shape from the new
// __BUILDER_DATA__ global so the existing renderers keep working unchanged.
// This is a temporary scaffold. The v2.0 state refactor (PRD §3, §9) will
// remove the legacy shape entirely and have every renderer read directly
// from __BUILDER_DATA__.steps[stepIndex].
(function _bridgeBuilderData() {
  const src = window.__BUILDER_DATA__;
  if (!src || !Array.isArray(src.steps)) return;
  const mapCats = cats => (cats || []).map(c => ({ categoryTitle: c.title, products: c.products }));
  // Phase 1 mapping: step 0 = bases, step 1 = charms.
  // The first non-draggable single-select step is the base; the first
  // draggable step is the charm step. Falls back to index 0/1 if absent.
  const baseStep = src.steps.find(s => !s.draggable) || src.steps[0];
  const charmStep = src.steps.find(s => s.draggable) || src.steps[1];
  window.__CHARM_BUILDER_DATA__ = {
    materialSwatches: src.materialSwatches || {},
    bases: mapCats(baseStep?.categories),
    charms: mapCats(charmStep?.categories),
    // Step heading + description, authored on the builder_step metaobject's
    // label / description fields. Rendered into each step's header shell.
    baseMeta: { label: baseStep?.label || "", description: baseStep?.description || "" },
    charmMeta: { label: charmStep?.label || "", description: charmStep?.description || "" },
  };
})();

/**
 * Tier thresholds — must mirror CharmBuilder.getDiscountPercent.
 * Module-scoped so it's available immediately on import; previously
 * inside _charmBuilderInit which created a TDZ ReferenceError when
 * renderDiscountBar fired from a setState dispatched during init.
 */
/**
 * PRD §4.9 — the current step is reflected in the URL as ?step=1|2|review
 * so browser back/forward steps through the builder instead of leaving the
 * page, and a step can be deep-linked directly. Module-scoped for the same
 * TDZ reason discussed above (CharmBuilder.getDiscountTiers() supplies the
 * tier ladder instead of a hardcoded module constant).
 */
const STEP_TO_PARAM = { 1: "1", 2: "2", 3: "review" };
const PARAM_TO_STEP = { 1: 1, 2: 2, review: 3 };

/**
 * Base products aren't consistent about what they call their length option —
 * seen in the wild: "Chain Length" (Charm Chains category), "Size" (a couple
 * of Charm Collectors necklaces). Matched case-insensitively in
 * _lengthOptionKey. Module-scoped for the same TDZ reason as above.
 */
const LENGTH_OPTION_ALIASES = ["chain length", "length", "size"];

/**
 * Fallback pendant/charm limit when the selected base variant has no (or an
 * invalid) custom.collectors_number_of_pendants_ metafield value. Module-
 * scoped for the same TDZ reason as above.
 */
const DEFAULT_MAX_CHARMS = 9;

function _charmBuilderInit() {
  if (!window.CharmBuilder || !window.__BUILDER_DATA__) return;

  // ---------------------------------------------------------------------------
  // Hoisted sidebar staging state — declared at the top of init so that
  // _renderAll and listener callbacks can reference them safely. (Their old
  // mid-file declarations created TDZ throws on first render.)
  // ---------------------------------------------------------------------------
  let _sidebarStaging = new Map();
  let _sidebarProductId = null;
  let _sidebarOpenMetal = null;

  // Remembers the last length the customer explicitly picked per base
  // product (Map<productId, lengthValue>), so switching away and back to a
  // base re-applies their choice instead of resetting to the first length.
  // Never persisted — in-session only, like the sidebar staging above.
  const _baseLengthPrefs = new Map();

  // Recomputes prev/next tabs-arrow visibility for a given tabs row. Wired
  // once per row (see _wireTabsArrows below); the wrap element itself is
  // static and never destroyed, so re-wiring on every render would stack up
  // duplicate listeners — instead each render calls the returned updater to
  // re-check overflow after the tab buttons are rebuilt.
  let _updateBaseTabsArrows = () => {};
  let _updateCharmTabsArrows = () => {};

  // URL <-> step sync bookkeeping. Restoring + sanitizing + defaulting state
  // below can each independently call setState, and every one synchronously
  // re-enters _renderAll (dispatchEvent is synchronous) — so _initComplete,
  // not "have we synced once yet", gates replaceState vs pushState: every
  // sync during init replaces the same entry, however many times it fires,
  // and only a sync after init has fully returned pushes a new one.
  // _isApplyingPopstate suppresses the sync while reacting to the browser's
  // own back/forward (which already owns that history entry).
  let _lastSyncedStep = null;
  let _isApplyingPopstate = false;
  let _initComplete = false;

  // ---------------------------------------------------------------------------
  // DOM refs — declared FIRST so _renderAll (called below) can reference them.
  // Previously declared mid-init, which created a TDZ ReferenceError when
  // _renderAll fired before this block. Sentry suppressed the throw, leaving
  // every step shell stuck in its default `hidden` state.
  // ---------------------------------------------------------------------------
  const _sidebarEl = document.getElementById("cb-sidebar");
  const _sidebarCloseBtn = document.getElementById("cb-sidebar-close");
  const _sidebarCtaBtn = document.getElementById("cb-sidebar-cta");
  const _backBtn = document.getElementById("cb-back-button");
  const _progressBackBtn = document.getElementById("cb-progress-back");

  // ---------------------------------------------------------------------------
  // Listeners — attached before any state mutation so setState dispatches
  // are not silently dropped.
  // ---------------------------------------------------------------------------
  document.addEventListener("charm-builder:updated", _renderAll);
  // VIP prices: the holiday gate script may finish after the builder's first render
  document.addEventListener("awe:holiday-gate:ready", () => {
    if (window.CharmBuilder && CharmBuilder.state) _renderAll();
  });

  document.addEventListener("charm-builder:cart-error", () => {
    const el = document.getElementById("cb-cart-error");
    if (!el) return;
    el.textContent = "Something went wrong. Please try again.";
    el.hidden = false;
  });

  // Browser back/forward — read the step the user landed on and sync state
  // to match, instead of leaving the page (there's no other listener for
  // this; the app never otherwise observes address-bar navigation).
  window.addEventListener("popstate", e => {
    const step = (e.state && e.state.cbStep) || _stepFromUrl() || 1;
    if (step === CharmBuilder.state.step) return;
    _isApplyingPopstate = true;
    CharmBuilder.setState({ step });
    _isApplyingPopstate = false;
  });

  // Reads ?step= from the current URL, mapped back to a numeric step.
  // Returns null when absent/unrecognized (caller decides the fallback).
  function _stepFromUrl() {
    const raw = new URLSearchParams(window.location.search).get("step");
    return PARAM_TO_STEP[raw] || null;
  }

  // Writes the given step into ?step=, leaving every other query param
  // (utm_*, gclid, fbclid, pb, etc.) untouched — ad links must keep working.
  // Every sync during init replaces the same history entry, no matter how
  // many times init's synchronous setState calls re-trigger it; only once
  // init has returned does a step change push a new entry (see
  // _initComplete above).
  function _syncUrlToStep(step) {
    const url = new URL(window.location.href);
    url.searchParams.set("step", STEP_TO_PARAM[step] || "1");
    const method = _initComplete ? "pushState" : "replaceState";
    history[method]({ cbStep: step }, "", url);
    _lastSyncedStep = step;
  }

  if (_sidebarCloseBtn) _sidebarCloseBtn.addEventListener("click", _closeSidebar);
  if (_sidebarCtaBtn) _sidebarCtaBtn.addEventListener("click", _commitSidebar);

  // Both back buttons (top-left of canvas + left of bottom CTA) decrement step.
  const _goBack = () => {
    const current = CharmBuilder.state.step;
    if (current > 1) CharmBuilder.setState({ step: current - 1 });
  };
  if (_backBtn) _backBtn.addEventListener("click", _goBack);
  if (_progressBackBtn) _progressBackBtn.addEventListener("click", _goBack);

  // Desktop-only prev/next arrows flanking a tabs row (mobile relies on
  // touch-scroll instead — see the CSS media query). Wires the click
  // handlers + a scroll listener ONCE against the tabs element itself
  // (never re-rendered, only its innerHTML is), and returns an updater the
  // caller re-invokes after each render to reflect the new content's
  // overflow (tab count can change what's scrollable).
  function _wireTabsArrows(tabsId) {
    const tabsEl = document.getElementById(tabsId);
    const nav = tabsEl?.closest(".charm-builder__tabs-nav");
    const wrap = tabsEl?.closest(".charm-builder__tabs-wrap");
    if (!tabsEl || !nav || !wrap) return () => {};

    const prevBtn = nav.querySelector(".charm-builder__tabs-arrow--prev");
    const nextBtn = nav.querySelector(".charm-builder__tabs-arrow--next");
    const SCROLL_AMOUNT = 200;

    const update = () => {
      const maxScroll = tabsEl.scrollWidth - tabsEl.clientWidth;
      const hasOverflow = maxScroll > 1;
      const atStart = tabsEl.scrollLeft <= 1;
      const atEnd = tabsEl.scrollLeft >= maxScroll - 1;
      if (prevBtn) prevBtn.hidden = !hasOverflow || atStart;
      if (nextBtn) nextBtn.hidden = !hasOverflow || atEnd;
      // Left shadow only makes sense once there's somewhere to scroll back
      // to — at rest it just sits on top of whatever's at the left edge
      // (e.g. an active tab's solid background).
      wrap.classList.toggle("is-scrolled", !atStart);
    };

    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        tabsEl.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        tabsEl.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
      });
    }
    tabsEl.addEventListener("scroll", update);
    window.addEventListener("resize", update);

    return update;
  }

  _updateBaseTabsArrows = _wireTabsArrows("cb-base-tabs");
  _updateCharmTabsArrows = _wireTabsArrows("cb-charm-tabs");

  // Step 3 "Edit" link — always returns to Step 1, not just one step back.
  const _reviewEditBtn = document.getElementById("cb-review-edit");
  if (_reviewEditBtn) {
    _reviewEditBtn.addEventListener("click", () => {
      CharmBuilder.setState({ step: 1 });
    });
  }
  if (_sidebarEl) {
    // Backdrop click — native dialogs surface backdrop clicks on the dialog itself.
    _sidebarEl.addEventListener("click", e => {
      if (e.target === _sidebarEl) _closeSidebar();
    });
    // Reset staging when the dialog closes (Escape key, .close(), backdrop click).
    // Closing without Add to Build discards staged edits — last committed state stands.
    _sidebarEl.addEventListener("close", () => {
      _sidebarStaging = new Map();
      _sidebarProductId = null;
      _sidebarOpenMetal = null;
      _sidebarEl.classList.remove("charm-builder__sidebar--length-mode");
    });
  }

  // ---------------------------------------------------------------------------
  // State hydration + initial render. setState below dispatches the
  // 'charm-builder:updated' event, which is now caught because the listener
  // is already attached.
  // ---------------------------------------------------------------------------
  _renderStepHeaders();

  CharmBuilder.restore();
  _sanitizeRestoredState();

  /**
   * Fill each step's heading + description from the builder_step metaobject
   * (label / description fields, serialised via __BUILDER_DATA__). Static
   * content — rendered once at init, not on every state update. Headers with
   * no authored label collapse via `hidden` so no empty gap renders.
   */
  function _renderStepHeaders() {
    const data = window.__CHARM_BUILDER_DATA__;
    if (!data) return;
    [
      { meta: data.baseMeta, headingId: "cb-step-1-heading", subId: "cb-step-1-subheading" },
      { meta: data.charmMeta, headingId: "cb-step-2-heading", subId: "cb-step-2-subheading" },
    ].forEach(({ meta, headingId, subId }) => {
      const headingEl = document.getElementById(headingId);
      const subEl = document.getElementById(subId);
      if (headingEl) {
        headingEl.textContent = meta?.label || "";
        headingEl.hidden = !meta?.label;
      }
      if (subEl) {
        subEl.textContent = meta?.description || "";
        subEl.hidden = !meta?.description;
      }
    });
  }

  {
    const patch = {};

    // PRD §4.9 — a ?step= in the URL (deep link / restored via back button
    // on a fresh load) wins over whatever step was persisted in localStorage.
    const urlStep = _stepFromUrl();
    if (urlStep && urlStep !== CharmBuilder.state.step) {
      patch.step = urlStep;
    }

    // Base PDP "Build My Story Set" CTA (blocks/awe-charm-builder-cta.liquid)
    // hands off a base variant as ?cb_base={variantId}. Swaps the base and
    // keeps any saved charms (an over-limit build shows the existing limit
    // warning), lands on Step 1, and switches the material filter to the
    // variant's material so the preselected card is visible. Invalid /
    // out-of-stock / not-a-base ids are a silent no-op. The param is removed
    // below either way so a reload can't re-apply it.
    const _cbBaseParam = new URLSearchParams(window.location.search).get("cb_base");
    const _cbCharmsParam = new URLSearchParams(window.location.search).get("cb_charms");
    if (_cbBaseParam) {
      const cbVariantId = parseInt(_cbBaseParam, 10);
      const cbProduct = _findBaseProductByVariantId(cbVariantId);
      const cbVariant = cbProduct && cbProduct.variants.find(v => v.id === cbVariantId);
      if (cbVariant && cbVariant.available !== false) {
        const lengthKey = _lengthOptionKey(cbProduct);
        if (lengthKey && cbVariant.options?.[lengthKey] != null) {
          _baseLengthPrefs.set(cbProduct.id, cbVariant.options[lengthKey]);
        }
        if (cbVariant.options?.Material) patch.activeMetal = cbVariant.options.Material;
        // Open the category tab holding this base so its card is on screen.
        const cbTab = (window.__CHARM_BUILDER_DATA__?.bases || []).findIndex(cat =>
          (cat.products || []).some(p => p.id === cbProduct.id)
        );
        if (cbTab >= 0) patch.activeBaseTab = cbTab;
        patch.step = 1;
        patch.base = {
          productId: cbProduct.id,
          variantId: cbVariant.id,
          title: cbProduct.title,
          variantTitle: cbVariant.title,
          image: cbVariant.image,
          featuredImage: cbVariant.featuredImage || cbVariant.image,
          openImage: cbVariant.openImage || null,
          price: cbVariant.price,
          url: cbProduct.url,
        };

        // Restore link (Klaviyo abandonment email — see
        // CharmBuilder.toRestoreUrl) also carries the charms. They REPLACE
        // the saved build rather than merge: the customer clicked a link to
        // this exact set. Only applied alongside a valid cb_base, since
        // charms can't be capped or placed without their base.
        if (_cbCharmsParam !== null) {
          const restored = _charmsFromRestoreParam(_cbCharmsParam);
          patch.charms = restored;
          if (restored.length) patch.step = 2;
        }
      } else {
        console.info("[charm-builder] cb_base not selectable, skipping preload:", _cbBaseParam);
      }
    }

    let effectiveMetal = patch.activeMetal || CharmBuilder.state.activeMetal;
    if (!effectiveMetal) {
      effectiveMetal = _extractMaterials()[0] || null;
      patch.activeMetal = effectiveMetal;
    }

    // Default Step 1's "Base" selection to the first in-stock product across
    // base categories so the grid/canvas never render blank on a fresh visit
    // (no localStorage yet) — and never auto-select a sold-out item. Mirrors
    // the activeMetal default above.
    if (!CharmBuilder.state.base && !patch.base) {
      const allBaseProducts = (window.__CHARM_BUILDER_DATA__?.bases || []).flatMap(cat => cat.products || []);
      const firstAvailable = allBaseProducts.find(p => {
        const v = _variantForMetal(p.variants, effectiveMetal) || p.variants[0];
        return v && v.available !== false;
      });
      const firstProduct = firstAvailable || allBaseProducts[0];
      const variant = firstProduct ? _resolveBaseVariant(firstProduct, effectiveMetal) : null;
      if (firstProduct && variant) {
        patch.base = {
          productId: firstProduct.id,
          variantId: variant.id,
          title: firstProduct.title,
          variantTitle: variant.title,
          image: variant.image,
          featuredImage: variant.featuredImage || variant.image,
          openImage: variant.openImage || null,
          price: variant.price,
          url: firstProduct.url,
        };
      }
    }

    if (Object.keys(patch).length) {
      CharmBuilder.setState(patch);
    } else {
      _renderAll();
    }

    if (_cbBaseParam !== null || _cbCharmsParam !== null) {
      // replaceState so back can't resurrect it; other params (utm_*, step…) survive.
      const url = new URL(window.location.href);
      url.searchParams.delete("cb_base");
      url.searchParams.delete("cb_charms");
      history.replaceState(history.state, "", url);
    }
  }

  // Every synchronous setState above has now run and re-entered _renderAll
  // as many times as it was going to. From here on, a step change is real
  // user/browser navigation and should push a new history entry.
  _initComplete = true;

  /**
   * Parse ?cb_charms= (variantId:x:y:rotation, comma-separated, one entry per
   * canvas unit) into charm records. Entries that are malformed, not a charm
   * in the builder data, sold out, or past the variant's stock are skipped —
   * the rest still restore. The base's pendant limit is deliberately NOT
   * enforced: an over-limit set comes back exactly as the customer left it
   * and the existing limit warning lets them choose which charm to remove
   * (same as an over-limit build after ?cb_base=). x/y are clamped to 0-100
   * (0:0 = not yet placed, so the canvas auto-places it as usual) and
   * rotation is wrapped to 0-359.
   */
  function _charmsFromRestoreParam(raw) {
    const qtyByVariant = new Map();
    const records = [];
    const clamp = n => Math.min(100, Math.max(0, n));

    raw.split(",").forEach((entry, i) => {
      const parts = entry.split(":").map(n => parseInt(n, 10));
      const [variantId, x, y, rotation] = parts;
      const skip = reason => console.info("[charm-builder] cb_charms entry skipped:", entry, reason);

      if (parts.length !== 4 || parts.some(n => Number.isNaN(n))) return skip("malformed");
      const product = _findCharmProductByVariantId(variantId);
      const variant = product && product.variants.find(v => v.id === variantId);
      if (!variant) return skip("not a charm");
      if (variant.available === false) return skip("sold out");
      const qty = (qtyByVariant.get(variantId) || 0) + 1;
      if (qty > _variantMaxQty(variant)) return skip("over stock");
      qtyByVariant.set(variantId, qty);

      records.push({
        ..._charmRecordFields(product, variant),
        instanceId: `charm-${Date.now()}-${Math.random().toString(36).slice(2, 7)}-${variantId}-${i}`,
        x: clamp(x),
        y: clamp(y),
        rotation: ((rotation % 360) + 360) % 360,
      });
    });

    return records;
  }

  /**
   * Validate restored localStorage state against the current __BUILDER_DATA__.
   * Persisted state can reference materials, products, or variants that no
   * longer exist if the metaobject changed since the customer's last visit.
   * Without this pass the page renders empty (most visible: Step 2 shows
   * "no products" because activeMetal isn't in any current variant).
   */
  function _sanitizeRestoredState() {
    const data = window.__CHARM_BUILDER_DATA__;
    if (!data) return;

    const materials = _extractMaterials();
    const patch = {};

    // 1. activeMetal — must exist in current variant options.
    const metal = CharmBuilder.state.activeMetal;
    if (metal && !materials.includes(metal)) {
      patch.activeMetal = materials[0] || null;
    }

    // 2. base — clear if its variant no longer exists in any base category.
    const base = CharmBuilder.state.base;
    if (base) {
      const baseVariant = _findBaseVariant(base.productId, base.variantId);
      if (!baseVariant) {
        patch.base = null;
        patch.step = 1; // Send them back to pick a new base.
      }
    }

    // 3. charms — drop entries whose product+variant pair is gone.
    const charms = CharmBuilder.state.charms || [];
    const liveCharms = charms.filter(c => !!_findCharmVariant(c.productId, c.variantId));
    if (liveCharms.length !== charms.length) {
      patch.charms = liveCharms;
    }

    // 4. activeCharmTab / activeBaseTab — clamp to valid index range.
    const baseCats = data.bases || [];
    const charmCats = data.charms || [];
    const baseTab = CharmBuilder.state.activeBaseTab || 0;
    const charmTab = CharmBuilder.state.activeCharmTab || 0;
    if (baseTab >= baseCats.length) patch.activeBaseTab = 0;
    if (charmTab >= charmCats.length) patch.activeCharmTab = 0;

    if (Object.keys(patch).length) {
      CharmBuilder.setState(patch);
    }
  }

  // ---------------------------------------------------------------------------
  // Step routing
  // ---------------------------------------------------------------------------

  function _renderAll() {
    const { step, activeMetal } = CharmBuilder.state;

    // PRD §4.9 — keep the URL's ?step= in lockstep with state.step so browser
    // back/forward works and the current step is deep-linkable. Skipped while
    // applying a popstate ourselves — that history entry already exists.
    if (step !== _lastSyncedStep) {
      if (_isApplyingPopstate) {
        _lastSyncedStep = step;
      } else {
        _syncUrlToStep(step);
      }
    }

    // PRD §8 — sidebar content is filtered by activeMetal; close if that changed.
    if (_sidebarEl?.open && _sidebarOpenMetal !== null && _sidebarOpenMetal !== activeMetal) {
      _closeSidebar();
    }

    renderStep(step);
    if (step === 1) {
      renderMaterialFilter();
      renderBaseGrid();
    }
    if (step === 2) {
      renderMaterialFilter();
      renderDiscountBar();
      renderCharmGrid();
    }
    if (step === 3) {
      renderDiscountBar();
      renderReview();
    }
    updateProgressBar(CharmBuilder.state);
    updateCanvas(CharmBuilder.state);
  }

  function renderStep(step) {
    [1, 2, 3].forEach(n => {
      const el = document.getElementById(`cb-step-${n}`);
      if (el) el.hidden = n !== step;
    });
    // Both back buttons (canvas top-left + bottom-CTA-row) hidden on step 1.
    const canvasBack = document.getElementById("cb-back-button");
    if (canvasBack) canvasBack.hidden = step <= 1;
    const progressBack = document.getElementById("cb-progress-back");
    if (progressBack) progressBack.hidden = step <= 1;
    // Charm counter badge (canvas top-right) — Step 2 only.
    const canvasCounter = document.getElementById("cb-canvas-charm-counter");
    if (canvasCounter) canvasCounter.hidden = step !== 2;
    // Surface the current step on the root container so CSS can show the
    // discount top bar on steps 2 + 3 only and adjust the mobile layout.
    const root = document.getElementById("charm-builder");
    if (root) root.dataset.cbStep = String(step);
  }

  // ---------------------------------------------------------------------------
  // Step 1 — base grid
  // ---------------------------------------------------------------------------

  // Card badge from the custom.charm_builder_badge metafield (blank → none).
  function _cardBadge(product) {
    const label = (product.badge || "").trim();
    if (!label) return "";
    const safe = label.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return `<span class="charm-builder__card-badge">${safe}</span>`;
  }

  function renderBaseGrid() {
    const tabsEl = document.getElementById("cb-base-tabs");
    const gridEl = document.getElementById("cb-base-grid");
    if (!tabsEl || !gridEl) return;

    const bases = window.__CHARM_BUILDER_DATA__.bases || [];
    if (!bases.length) {
      gridEl.innerHTML = '<p class="charm-builder__empty">No base products found.</p>';
      return;
    }

    const activeTab = CharmBuilder.state.activeBaseTab || 0;

    // Tabs
    tabsEl.innerHTML = bases
      .map(
        (cat, i) => `
      <button
        class="charm-builder__tab${i === activeTab ? " is-active" : ""}"
        data-tab="${i}"
        role="tab"
        aria-selected="${i === activeTab}"
        type="button"
      >${cat.categoryTitle}</button>
    `
      )
      .join("");

    tabsEl.querySelectorAll(".charm-builder__tab").forEach(btn => {
      btn.addEventListener("click", () => {
        CharmBuilder.setState({ activeBaseTab: parseInt(btn.dataset.tab, 10) });
      });
    });
    _updateBaseTabsArrows();

    // Cards — filter to products with a matching variant when a metal is active
    // (mirrors renderCharmGrid), and hide sold-out bases entirely rather than
    // showing a disabled state. Checks ALL matching-material variants, not
    // just the first one (_variantForMetal) — a chain with several lengths
    // should still show as long as ANY length in this material is in stock,
    // not only when its first-listed length happens to be the available one.
    const allProducts = bases[activeTab]?.products || [];
    const activeMetal = CharmBuilder.state.activeMetal;
    const products = allProducts.filter(p => {
      const matching = activeMetal ? p.variants.filter(v => v.options?.Material === activeMetal) : p.variants;
      return matching.some(v => v.available !== false);
    });
    const selectedId = CharmBuilder.state.base?.productId;

    if (!products.length) {
      gridEl.innerHTML = '<p class="charm-builder__empty">No bases available in this material.</p>';
      return;
    }

    gridEl.innerHTML = products
      .map(product => {
        // Length-aware: respects a previously-chosen length for this product,
        // else defaults to the first available length. See _resolveBaseVariant.
        const variant = _resolveBaseVariant(product, activeMetal);
        if (!variant) return "";
        const isSelected = product.id === selectedId;

        // Not every base has a length option (chains do, most single
        // necklaces don't) — only offer the picker when there's actually
        // more than one length to choose from for the current material.
        const lengthKey = _lengthOptionKey(product);
        const availableLengths = lengthKey
          ? [
              ...new Set(
                product.variants
                  .filter(v => (!activeMetal || v.options?.Material === activeMetal) && v.available !== false)
                  .map(v => v.options[lengthKey])
              ),
            ]
          : [];
        const showLengthPicker = isSelected && availableLengths.length > 1;
        // Once the customer has explicitly picked a length (vs. just
        // defaulting to the first available one), the button becomes a
        // filled label showing that choice instead of a plain prompt.
        const lengthChosen = lengthKey && _baseLengthPrefs.has(product.id);
        const lengthValue = lengthKey ? variant.options?.[lengthKey] : null;

        return `
        <div
          class="charm-builder__card${isSelected ? " is-selected" : ""}"
          role="listitem"
          data-product-id="${product.id}"
          data-variant-id="${variant.id}"
          tabindex="0"
          aria-label="${product.title}${isSelected ? ", selected" : ""}"
        >
          <div class="charm-builder__card-image-wrap">
            <img
              class="charm-builder__card-img"
              src="${variant.featuredImage || variant.image}"
              alt="${product.title}"
              loading="lazy"
              width="300"
              height="300"
            >
            ${_cardBadge(product)}
            <div class="charm-builder__card-check" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 8L6.5 11.5L13 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            ${
              showLengthPicker
                ? lengthChosen && lengthValue
                  ? `<button class="charm-builder__select-length charm-builder__select-length--selected" type="button" data-action="select-length" aria-label="Change length for ${product.title}, currently ${lengthValue}">Selected: ${lengthValue}</button>`
                  : `<button class="charm-builder__select-length" type="button" data-action="select-length" aria-label="Select length for ${product.title}">Select Length</button>`
                : ""
            }
          </div>
          <div class="charm-builder__card-info">
            <p class="charm-builder__card-title">${product.title}</p>
            <p class="charm-builder__card-price">${_cardPriceHtml(product, variant.price)}</p>
          </div>
        </div>
      `;
      })
      .join("");

    gridEl.querySelectorAll(".charm-builder__card").forEach(card => {
      const productId = parseInt(card.dataset.productId, 10);

      const select = () => {
        const variantId = parseInt(card.dataset.variantId, 10);
        const product = products.find(p => p.id === productId);
        const variant = product?.variants.find(v => v.id === variantId);
        if (!product || !variant) return;
        _selectBase(product, variant);
      };

      const lengthBtn = card.querySelector('[data-action="select-length"]');
      if (lengthBtn) {
        lengthBtn.addEventListener("click", e => {
          e.stopPropagation();
          renderLengthSidebar(productId);
        });
      }

      card.addEventListener("click", select);
      card.addEventListener("keydown", e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          select();
        }
      });
    });
  }

  // Shared by the base grid's card click and the length sidebar's row click —
  // both ultimately just set state.base to a specific product+variant.
  function _selectBase(product, variant) {
    CharmBuilder.setState({
      base: {
        productId: product.id,
        variantId: variant.id,
        title: product.title,
        variantTitle: variant.title,
        // Canvas overlay uses the transparent-fallback image (variant.image);
        // product card uses variant.featuredImage. Store both on state so
        // each surface can pick the right one.
        image: variant.image,
        featuredImage: variant.featuredImage || variant.image,
        // "Open slots" artwork — null unless the base variant has the
        // charm_builder_open_image metafield set. See _renderCanvasBase.
        openImage: variant.openImage || null,
        price: variant.price,
        url: product.url,
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Step 2 — material filter (also visible on Step 1)
  // ---------------------------------------------------------------------------

  function renderMaterialFilter() {
    // Both Step 1 and Step 2 contain a .charm-builder__material-filter container.
    // We render identical content into each so whichever step is visible has the
    // filter populated. Using a class instead of an ID avoids duplicate-ID HTML.
    const containers = document.querySelectorAll(".charm-builder__material-filter");
    if (!containers.length) return;

    // Scope the swatch list to the active tab's own products — a Silver swatch
    // shouldn't appear (or be selectable) while browsing a category that has
    // no Silver products, even if some other category does.
    const step = CharmBuilder.state.step;
    const scopedProducts =
      step === 2
        ? window.__CHARM_BUILDER_DATA__?.charms?.[CharmBuilder.state.activeCharmTab || 0]?.products || []
        : window.__CHARM_BUILDER_DATA__?.bases?.[CharmBuilder.state.activeBaseTab || 0]?.products || [];

    const materials = _extractMaterialsFromProducts(scopedProducts);
    let active = CharmBuilder.state.activeMetal;

    // The active metal isn't offered in this tab (e.g. just switched category)
    // — fall back to the first material this tab does have, same pattern as
    // the init/restore defaulting above. setState re-triggers this render.
    if (materials.length && !materials.includes(active)) {
      CharmBuilder.setState({ activeMetal: materials[0] });
      return;
    }

    const swatchMap = window.__CHARM_BUILDER_DATA__?.materialSwatches || {};

    const html = !materials.length
      ? ""
      : `
      <span class="charm-builder__material-label">Materials:</span>
      <div class="charm-builder__material-swatches" role="radiogroup" aria-label="Select material">
        ${materials
          .map(
            m => `
          <button
            class="charm-builder__swatch${m === active ? " is-active" : ""}"
            data-material="${m}"
            role="radio"
            aria-checked="${m === active}"
            aria-label="${m}"
            type="button"
            title="${m}"
          ><span class="charm-builder__swatch-dot" style="background:${swatchMap[m] || _swatchFallback(m)};" aria-hidden="true"></span></button>
        `
          )
          .join("")}
      </div>
      <span class="charm-builder__swatch-selected-name" aria-live="polite">${active || ""}</span>
      <button
        type="button"
        class="awe-sidebar-trigger charm-builder__material-info-btn button button-unstyled"
        data-section-id="awe-guide-material"
        aria-label="Material information"
      ><svg class="icon icon-info" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="6.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)"/>
        <path d="M10 9.5V13" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
        <circle cx="10" cy="7.25" r="0.75" fill="currentColor"/>
      </svg></button>
    `;

    containers.forEach(el => {
      el.hidden = !materials.length;
      el.innerHTML = html;
      el.querySelectorAll(".charm-builder__swatch").forEach(btn => {
        btn.addEventListener("click", () => {
          CharmBuilder.setState({ activeMetal: btn.dataset.material });
        });
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Step 2 — discount tier progress bar
  // ---------------------------------------------------------------------------

  function _totalCharmCount() {
    return CharmBuilder.state.charms.reduce((sum, c) => sum + Math.max(1, c.quantity || 1), 0);
  }

  /**
   * Returns the next tier to unlock, or null when the highest tier is
   * already reached. "Next" = lowest-percent tier strictly greater than the
   * current discount (tiers sorted ascending since a merchandiser could
   * author them in any order in Admin).
   */
  function _nextTier(count, currentPct) {
    const tiers = [...CharmBuilder.getDiscountTiers()].sort((a, b) => a.percent - b.percent);
    return tiers.find(t => t.percent > currentPct) || null;
  }

  // Copy matches Figma 7603-57017 / 8277-58586 ("Add 1 more charm to unlock 10% off!").
  function _discountMessage(count, currentPct) {
    const next = _nextTier(count, currentPct);
    if (!next) return `You\u2019ve unlocked ${currentPct}% off!`;
    const needed = Math.max(0, next.threshold - count);
    const noun = needed === 1 ? "charm" : "charms";
    return count === 0
      ? `Add ${next.threshold} ${noun} to unlock ${next.percent}% off!`
      : `Add ${needed} more ${noun} to unlock ${next.percent}% off!`;
  }

  function renderDiscountBar() {
    // Single full-width top bar (shown on Steps 2 + 3 via CSS). Class-based
    // lookup kept so any extra instance would stay in sync from the same state.
    const bars = document.querySelectorAll(".charm-builder__discount-bar");
    if (!bars.length) return;
    bars.forEach(bar => { bar.hidden = !!_vipGate(); });
    if (_vipGate()) return;

    const count = _totalCharmCount();
    const currentPct = CharmBuilder.getDiscountPercent();
    const message = _discountMessage(count, currentPct);
    // Tier is set by charm count, but the percent applies to base + charms.
    const savingsCents = CharmBuilder.getDiscountCents();

    bars.forEach(bar => {
      const msgEl = bar.querySelector(".charm-builder__discount-message");
      if (msgEl) msgEl.textContent = message;

      const savingsEl = bar.querySelector(".charm-builder__discount-savings");
      if (savingsEl) {
        savingsEl.hidden = savingsCents <= 0;
        savingsEl.textContent = savingsCents > 0 ? `You\u2019re Saving ${_formatPrice(savingsCents)}` : "";
      }

      // Mark each tier node as reached based on charm count vs threshold.
      const nodes = bar.querySelectorAll(".charm-builder__discount-node");
      const reachedFlags = [];
      nodes.forEach(node => {
        const threshold = parseInt(node.dataset.threshold, 10);
        const reached = count >= threshold;
        node.classList.toggle("is-reached", reached);
        reachedFlags.push(reached);
      });

      // A connector line is reached when both adjacent nodes are reached.
      const lines = bar.querySelectorAll(".charm-builder__discount-node-line");
      lines.forEach((line, i) => {
        const lineReached = reachedFlags[i] && reachedFlags[i + 1];
        line.classList.toggle("is-reached", !!lineReached);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Step 2 — charm grid
  // ---------------------------------------------------------------------------

  function renderCharmGrid() {
    const tabsEl = document.getElementById("cb-charm-tabs");
    const gridEl = document.getElementById("cb-charm-grid");
    const countEl = document.getElementById("cb-charm-count");
    if (!tabsEl || !gridEl) return;

    const charms = window.__CHARM_BUILDER_DATA__.charms || [];
    const activeTab = CharmBuilder.state.activeCharmTab || 0;
    const activeMetal = CharmBuilder.state.activeMetal;

    // Helper count — total quantity across all charm instances, against the
    // selected base's pendant limit (custom.collectors_number_of_pendants_).
    const total = _totalCharmCount();
    const maxCharms = _maxPendantsForBase();

    if (countEl) countEl.textContent = total;
    const maxEl = document.getElementById("cb-charm-max");
    if (maxEl) maxEl.textContent = maxCharms;
    const countMaxEl = document.getElementById("cb-charm-count-max");
    if (countMaxEl) countMaxEl.textContent = maxCharms;

    const limitWarningEl = document.getElementById("cb-charm-limit-warning");
    if (limitWarningEl) {
      const exceeds = total > maxCharms;
      limitWarningEl.hidden = !exceeds;
      if (exceeds) {
        limitWarningEl.textContent = `You've selected ${total} pieces — this base holds up to ${maxCharms}.`;
      }
    }

    if (!charms.length) {
      tabsEl.innerHTML = "";
      gridEl.innerHTML = '<p class="charm-builder__empty">No charm products found.</p>';
      _updateCharmTabsArrows();
      return;
    }

    // Tabs
    tabsEl.innerHTML = charms
      .map(
        (cat, i) => `
      <button
        class="charm-builder__tab${i === activeTab ? " is-active" : ""}"
        data-tab="${i}"
        role="tab"
        aria-selected="${i === activeTab}"
        type="button"
      >${cat.categoryTitle}</button>
    `
      )
      .join("");

    tabsEl.querySelectorAll(".charm-builder__tab").forEach(btn => {
      btn.addEventListener("click", () => {
        CharmBuilder.setState({ activeCharmTab: parseInt(btn.dataset.tab, 10) });
      });
    });
    _updateCharmTabsArrows();

    // Cards — filter to products with a matching variant when a metal is
    // active. A sold-out product is only hidden entirely when it has just
    // one option for this material (nothing to fall back to); a product with
    // multiple options always shows, even if every option is currently
    // sold out, since opening it still reveals the sidebar's other options
    // (each shown disabled there when unavailable).
    const allProducts = charms[activeTab]?.products || [];
    const products = allProducts.filter(p => {
      const matching = activeMetal ? p.variants.filter(v => v.options?.Material === activeMetal) : p.variants;
      if (!matching.length) return false;
      if (matching.length > 1) return true;
      return matching[0].available !== false;
    });

    if (!products.length) {
      gridEl.innerHTML = '<p class="charm-builder__empty">No charms available in this material.</p>';
      return;
    }

    gridEl.innerHTML = products
      .map(product => {
        const matchingVariants = activeMetal
          ? product.variants.filter(v => v.options?.Material === activeMetal)
          : product.variants;
        const availableVariants = matchingVariants.filter(v => v.available !== false);
        // Prefer an available variant as the card's image/price
        // representative; fall back to any matching variant so a
        // multi-option product that's fully sold out for this material can
        // still render (its "Add Style" click opens the sidebar, which shows
        // every option, each disabled where unavailable).
        const variant = availableVariants[0] || matchingVariants[0];
        if (!variant) return "";
        const isMultiVariant = matchingVariants.length > 1;

        const productCharms = CharmBuilder.state.charms.filter(c => c.productId === product.id);
        const hasSelection = productCharms.length > 0;
        const distinctSelected = new Set(productCharms.map(c => c.variantId)).size;
        const totalQty = productCharms.reduce((s, c) => s + (c.quantity || 1), 0);

        // Three card states:
        //   1. No selection  → circled-plus "Add Style" icon button
        //   2. Multi-variant + selected → "(N) SELECTED" pill (click reopens sidebar)
        //   3. Single-variant + selected → qty stepper (− N +)
        let actionHtml;
        if (!hasSelection) {
          actionHtml = `
            <button
              class="charm-builder__select-style"
              type="button"
              data-action="select-style"
              aria-label="Add style for ${product.title}"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
                <path d="M5.66667 9H6.33333V6.33333H9V5.66667H6.33333V3H5.66667V5.66667H3V6.33333H5.66667V9ZM6.00217 12C5.1725 12 4.39244 11.8426 3.662 11.5277C2.93167 11.2128 2.29633 10.7854 1.756 10.2457C1.21567 9.70589 0.787944 9.07111 0.472833 8.34133C0.157611 7.61167 0 6.83194 0 6.00217C0 5.1725 0.157444 4.39244 0.472333 3.662C0.787222 2.93167 1.21456 2.29633 1.75433 1.756C2.29411 1.21567 2.92889 0.787944 3.65867 0.472833C4.38833 0.157611 5.16806 0 5.99783 0C6.8275 0 7.60756 0.157445 8.338 0.472334C9.06833 0.787223 9.70367 1.21456 10.244 1.75433C10.7843 2.29411 11.2121 2.92889 11.5272 3.65867C11.8424 4.38833 12 5.16806 12 5.99783C12 6.8275 11.8426 7.60756 11.5277 8.338C11.2128 9.06833 10.7854 9.70367 10.2457 10.244C9.70589 10.7843 9.07111 11.2121 8.34133 11.5272C7.61167 11.8424 6.83194 12 6.00217 12ZM6 11.3333C7.48889 11.3333 8.75 10.8167 9.78333 9.78333C10.8167 8.75 11.3333 7.48889 11.3333 6C11.3333 4.51111 10.8167 3.25 9.78333 2.21667C8.75 1.18333 7.48889 0.666667 6 0.666667C4.51111 0.666667 3.25 1.18333 2.21667 2.21667C1.18333 3.25 0.666667 4.51111 0.666667 6C0.666667 7.48889 1.18333 8.75 2.21667 9.78333C3.25 10.8167 4.51111 11.3333 6 11.3333Z" fill="currentColor"/>
              </svg>
            </button>
          `;
        } else if (isMultiVariant) {
          actionHtml = `
            <button
              class="charm-builder__selected-pill"
              type="button"
              data-action="edit-selection"
              aria-label="${distinctSelected} ${
                distinctSelected === 1 ? "variant" : "variants"
              } selected, edit selection for ${product.title}"
            >(${distinctSelected}) Selected</button>
          `;
        } else {
          // Cap is per exact variant (not just product) — a customer can't
          // add more of this variant than Shopify's own inventory would
          // allow at checkout. Scoped to variant.id rather than the
          // productCharms total above so a stray record under a different
          // material (see isMultiVariant note) never falsely blocks this one.
          const variantMaxQty = _variantMaxQty(variant);
          const variantQty = _charmVariantQty(variant.id);
          const atMax = variantQty >= variantMaxQty;
          actionHtml = `
            <div class="charm-builder__qty-stepper" role="group" aria-label="Quantity">
              <button
                class="charm-builder__qty-btn charm-builder__qty-btn--minus"
                type="button"
                aria-label="Remove one"
                data-action="decrement"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M7 12h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
              <span class="charm-builder__qty-count">${totalQty}</span>
              <button
                class="charm-builder__qty-btn charm-builder__qty-btn--plus"
                type="button"
                aria-label="${atMax ? "No more in stock" : "Add one more"}"
                data-action="increment"
                ${atMax ? "disabled" : ""}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M12 7v10M7 12h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          `;
        }

        // The card's <wishlist-button-collection> is Wishlist King's own
        // custom element (registered globally by the app embed). It
        // self-initializes on insertion — including after every innerHTML
        // re-render of this grid — and shares state with the hearts on
        // collection cards. If the app embed is disabled the tag never
        // upgrades and CSS (:not(:defined)) hides it, so the builder
        // degrades gracefully with no errors.
        return `
        <div
          class="charm-builder__card charm-builder__card--charm${hasSelection ? " is-selected" : ""}"
          role="listitem"
          data-product-id="${product.id}"
          data-variant-id="${variant.id}"
          data-variant-count="${matchingVariants.length}"
          aria-label="${product.title}${hasSelection ? `, ${distinctSelected} selected` : ""}"
        >
          <div class="charm-builder__card-image-wrap">
            <img
              class="charm-builder__card-img"
              src="${variant.featuredImage || variant.image}"
              alt="${product.title}"
              loading="lazy"
              width="156"
              height="156"
            >
            ${_cardBadge(product)}
            <wishlist-button-collection
              class="charm-builder__card-wishlist"
              data-product-handle="${product.handle}"
              data-variant-id="${variant.id}"
              show-icon
              icon="wishlist"
            ></wishlist-button-collection>
            ${actionHtml}
          </div>
          <div class="charm-builder__card-info">
            <p class="charm-builder__card-title">${product.title}</p>
            <p class="charm-builder__card-price">${_cardPriceHtml(product, variant.price)}</p>
          </div>
        </div>
      `;
      })
      .join("");

    gridEl.querySelectorAll(".charm-builder__card").forEach(card => {
      const productId = parseInt(card.dataset.productId, 10);
      const variantId = parseInt(card.dataset.variantId, 10);

      const selectBtn = card.querySelector('[data-action="select-style"]');
      if (selectBtn) {
        selectBtn.addEventListener("click", e => {
          e.stopPropagation();
          _onSelectStyle(productId);
        });
      }

      const editBtn = card.querySelector('[data-action="edit-selection"]');
      if (editBtn) {
        editBtn.addEventListener("click", e => {
          e.stopPropagation();
          renderSidebar(productId);
        });
      }

      const incBtn = card.querySelector('[data-action="increment"]');
      if (incBtn) {
        incBtn.addEventListener("click", e => {
          e.stopPropagation();
          _changeCharmQty(productId, variantId, +1);
        });
      }

      const decBtn = card.querySelector('[data-action="decrement"]');
      if (decBtn) {
        decBtn.addEventListener("click", e => {
          e.stopPropagation();
          _changeCharmQty(productId, variantId, -1);
        });
      }

      // Wishlist King handles the heart's own toggle internally — this only
      // keeps the click from ever reaching card-level handlers (bubble phase;
      // analytics listens in capture phase and still sees it).
      const wishlistEl = card.querySelector(".charm-builder__card-wishlist");
      if (wishlistEl) {
        wishlistEl.addEventListener("click", e => e.stopPropagation());
      }

      // Whole card is a larger pointer target for the card's own action:
      // "Add Style" when nothing is selected, "Edit selection" for a selected
      // multi-variant product. A selected single-variant card does nothing —
      // quantity only changes via the − N + stepper. Keyboard users keep the
      // dedicated buttons (no tabindex on the card, avoiding nested controls).
      card.addEventListener("click", () => {
        if (selectBtn) _onSelectStyle(productId);
        else if (editBtn) renderSidebar(productId);
      });
    });
  }

  // Select Style click — single-variant products skip the sidebar and add directly.
  function _onSelectStyle(productId) {
    const product = _findCharmProductById(productId);
    if (!product) return;

    const activeMetal = CharmBuilder.state.activeMetal;
    const matching = activeMetal ? product.variants.filter(v => v.options?.Material === activeMetal) : product.variants;
    // Out-of-stock variants are never selectable — mirrors the grid card and
    // sidebar, which both hide them entirely rather than showing them disabled.
    const available = matching.filter(v => v.available !== false);

    if (available.length === 1) {
      const v = available[0];
      CharmBuilder.addCharm(_charmRecordFields(product, v));
      return;
    }

    renderSidebar(productId);
  }

  // ---------------------------------------------------------------------------
  // Sidebar
  // ---------------------------------------------------------------------------

  // Local sidebar session state — hoisted to the top of init (see top of
  // _charmBuilderInit). Map<variantId, quantity>. quantity > 0 means the
  // variant is selected.

  function renderSidebar(productId) {
    const dialog = document.getElementById("cb-sidebar");
    if (!dialog) return;

    const product = _findCharmProductById(productId);
    if (!product) return;

    // Reset in case the length sidebar (below) left it in single-select mode.
    dialog.classList.remove("charm-builder__sidebar--length-mode");
    const titleEl = document.getElementById("cb-sidebar-title");
    if (titleEl) titleEl.textContent = "Select your style";

    const activeMetal = CharmBuilder.state.activeMetal;
    // Sold-out variants stay in the list here (unlike the grid, which hides
    // a product entirely if nothing's available) — shown disabled with an
    // "Out of Stock" label so the customer can see the option exists.
    const variants = activeMetal ? product.variants.filter(v => v.options?.Material === activeMetal) : product.variants;

    // Pre-fill staging from existing instances so reopening is true edit mode.
    // Each charm record is one unit (qty 1), so we count records per variantId.
    _sidebarStaging = new Map();
    _sidebarProductId = productId;
    _sidebarOpenMetal = activeMetal;
    CharmBuilder.state.charms.forEach(c => {
      if (c.productId === productId) {
        const current = _sidebarStaging.get(c.variantId) || 0;
        _sidebarStaging.set(c.variantId, current + (c.quantity || 1));
      }
    });

    const subEl = document.getElementById("cb-sidebar-subtitle");
    if (subEl) subEl.textContent = `Select your ${product.title}`;

    const listEl = document.getElementById("cb-sidebar-content");
    if (listEl) {
      if (!variants.length) {
        listEl.innerHTML = '<p class="charm-builder__sidebar-empty">No options available in this material.</p>';
      } else {
        listEl.innerHTML = variants.map(_renderSidebarRow).join("");
        _wireSidebarRowEvents(listEl);
      }
    }

    _updateSidebarCta();

    if (!dialog.open) dialog.showModal();
  }

  // Base "SELECT LENGTH" sidebar — single-select. Picking a row applies it
  // immediately (live preview on the canvas) but keeps the sidebar open;
  // only the shared footer CTA ("Add to Build", see _commitSidebar's
  // length-mode branch) closes it. Reuses the same #cb-sidebar dialog shell
  // as the multi-select charm sidebar above.
  function renderLengthSidebar(productId) {
    const dialog = document.getElementById("cb-sidebar");
    if (!dialog) return;

    const product = _findBaseProductById(productId);
    if (!product) return;

    const lengthKey = _lengthOptionKey(product);
    if (!lengthKey) return;

    dialog.classList.add("charm-builder__sidebar--length-mode");
    const titleEl = document.getElementById("cb-sidebar-title");
    if (titleEl) titleEl.textContent = "Select length";

    const activeMetal = CharmBuilder.state.activeMetal;
    const matching = activeMetal ? product.variants.filter(v => v.options?.Material === activeMetal) : product.variants;
    const variants = matching.filter(v => v.available !== false);

    _sidebarProductId = productId;
    _sidebarOpenMetal = activeMetal;

    const subEl = document.getElementById("cb-sidebar-subtitle");
    if (subEl) subEl.textContent = product.title;

    // Reflects whatever's currently applied — first-available on a fresh
    // pick, or the customer's remembered length if they've chosen one before
    // (see _resolveBaseVariant) — so the right row is pre-checked on open.
    const currentVariantId = CharmBuilder.state.base?.variantId;

    const listEl = document.getElementById("cb-sidebar-content");
    if (listEl) {
      if (!variants.length) {
        listEl.innerHTML = '<p class="charm-builder__sidebar-empty">No lengths available in this material.</p>';
      } else {
        listEl.innerHTML = variants.map(v => _renderLengthSidebarRow(v, lengthKey, v.id === currentVariantId)).join("");
        listEl.querySelectorAll('[data-action="select-length-option"]').forEach(btn => {
          btn.addEventListener("click", () => {
            const variantId = parseInt(btn.dataset.variantId, 10);
            const variant = variants.find(v => v.id === variantId);
            if (!variant) return;
            _baseLengthPrefs.set(product.id, variant.options[lengthKey]);
            _selectBase(product, variant);
            // Re-render (not close) so the newly-picked row shows checked —
            // only the footer CTA below closes this sidebar.
            renderLengthSidebar(productId);
          });
        });
      }
    }

    const ctaBtn = document.getElementById("cb-sidebar-cta");
    if (ctaBtn) {
      ctaBtn.textContent = "Add to Build";
      ctaBtn.disabled = false;
    }

    if (!dialog.open) dialog.showModal();
  }

  function _renderLengthSidebarRow(v, lengthKey, isCurrent) {
    const label = v.options?.[lengthKey] || v.title;
    return `
      <button
        type="button"
        class="charm-builder__sidebar-row charm-builder__sidebar-row--length${isCurrent ? " is-checked" : ""}"
        data-action="select-length-option"
        data-variant-id="${v.id}"
        aria-pressed="${isCurrent}"
      >
        <span class="charm-builder__sidebar-row-label">${label}</span>
        <span class="charm-builder__sidebar-checkbox-box" aria-hidden="true">
          <svg class="charm-builder__sidebar-checkbox-tick" viewBox="0 0 10 10" aria-hidden="true">
            <path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
      </button>
    `;
  }

  function _renderSidebarRow(v) {
    const available = v.available !== false;
    const label = (_variantOptionLabel(v) || v.title) + (available ? "" : " - Out of Stock");
    const qty = _sidebarStaging.get(v.id) || 0;
    const checked = qty > 0;
    const disabledAttr = available ? "" : "disabled";
    // − is disabled at qty 1 (floor). Stepper itself is hidden when unchecked via CSS,
    // but we still set the correct initial state in case of edit-mode reopen at qty=1.
    const decDisabledAttr = !available || qty <= 1 ? "disabled" : "";
    // + stops at this variant's inventory — mirrors Shopify's own add-to-cart
    // rule so the button disables here instead of the cart add failing later.
    const incDisabledAttr = !available || qty >= _variantMaxQty(v) ? "disabled" : "";

    // Row is a <div>, not a <label> — the checkbox <label> is scoped tightly so the
    // qty stepper buttons don't toggle the checkbox via label-association.
    return `
      <div
        class="charm-builder__sidebar-row${available ? "" : " is-unavailable"}${checked ? " is-checked" : ""}"
        data-variant-id="${v.id}"
      >
        <span class="charm-builder__sidebar-row-label">${label}</span>
        <div
          class="charm-builder__sidebar-row-stepper"
          role="group"
          aria-label="Quantity for ${label}"
        >
          <button
            class="charm-builder__sidebar-stepper-btn"
            type="button"
            data-action="row-decrement"
            aria-label="Decrease ${label}"
            ${decDisabledAttr}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
              <path d="M7 12h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <span class="charm-builder__sidebar-stepper-count" data-role="qty">${qty}</span>
          <button
            class="charm-builder__sidebar-stepper-btn"
            type="button"
            data-action="row-increment"
            aria-label="${qty >= _variantMaxQty(v) && available ? "No more in stock" : `Increase ${label}`}"
            ${incDisabledAttr}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
              <path d="M12 7v10M7 12h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <label class="charm-builder__sidebar-checkbox">
          <input
            type="checkbox"
            class="charm-builder__sidebar-checkbox-input"
            data-variant-id="${v.id}"
            ${checked ? "checked" : ""}
            ${disabledAttr}
            aria-label="${label}"
          >
          <span class="charm-builder__sidebar-checkbox-box" aria-hidden="true">
            <svg class="charm-builder__sidebar-checkbox-tick" viewBox="0 0 10 10" aria-hidden="true">
              <path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </label>
      </div>
    `;
  }

  function _wireSidebarRowEvents(listEl) {
    listEl.querySelectorAll(".charm-builder__sidebar-row").forEach(row => {
      const vid = parseInt(row.dataset.variantId, 10);

      const incBtn = row.querySelector('[data-action="row-increment"]');
      if (incBtn) incBtn.addEventListener("click", () => _changeStagingQty(vid, +1));

      const decBtn = row.querySelector('[data-action="row-decrement"]');
      if (decBtn) decBtn.addEventListener("click", () => _changeStagingQty(vid, -1));

      const cb = row.querySelector(".charm-builder__sidebar-checkbox-input");
      if (cb) {
        cb.addEventListener("change", () => {
          if (cb.checked) {
            if (!_sidebarStaging.get(vid)) _sidebarStaging.set(vid, 1);
          } else {
            _sidebarStaging.delete(vid);
          }
          _refreshSidebarRow(row, vid);
          _updateSidebarCta();
        });
      }
    });
  }

  function _changeStagingQty(vid, delta) {
    const current = _sidebarStaging.get(vid) || 0;
    // Stepper is hidden until the variant is checked; once staged, minimum is 1.
    // To remove the variant, the user unchecks the box.
    if (current === 0) return;
    if (delta > 0) {
      // Staged qty already represents the full desired count for this
      // variant (commit replaces, not adds), so cap it directly against
      // inventory — mirrors Shopify's own add-to-cart rule.
      const variant = _findCharmVariant(_sidebarProductId, vid);
      if (current >= _variantMaxQty(variant)) return;
    }
    const next = Math.max(1, current + delta);
    _sidebarStaging.set(vid, next);

    const row = document.querySelector(`.charm-builder__sidebar-row[data-variant-id="${vid}"]`);
    if (row) _refreshSidebarRow(row, vid);
    _updateSidebarCta();
  }

  function _refreshSidebarRow(row, vid) {
    const qty = _sidebarStaging.get(vid) || 0;
    const countEl = row.querySelector('[data-role="qty"]');
    if (countEl) countEl.textContent = qty;
    const cb = row.querySelector(".charm-builder__sidebar-checkbox-input");
    if (cb) cb.checked = qty > 0;
    row.classList.toggle("is-checked", qty > 0);
    // Floor the minus button at qty=1 — user removes via the checkbox.
    const decBtn = row.querySelector('[data-action="row-decrement"]');
    if (decBtn) decBtn.disabled = qty <= 1;
    // Cap the plus button at this variant's inventory.
    const variant = _findCharmVariant(_sidebarProductId, vid);
    const incBtn = row.querySelector('[data-action="row-increment"]');
    if (incBtn) incBtn.disabled = qty >= _variantMaxQty(variant);
  }

  function _updateSidebarCta() {
    const cta = document.getElementById("cb-sidebar-cta");
    if (!cta) return;

    let distinct = 0;
    let totalCents = 0;
    const product = _findCharmProductById(_sidebarProductId);
    if (product) {
      _sidebarStaging.forEach((qty, vid) => {
        if (qty <= 0) return;
        distinct++;
        const v = product.variants.find(vv => vv.id === vid);
        if (v) totalCents += _vipUnitPrice(product, v.price) * qty;
      });
    }

    // Was this sidebar opened to edit an existing selection? If so, an
    // empty staging is a valid intent: "remove all selections for this product."
    const hasExistingForProduct = CharmBuilder.state.charms.some(c => c.productId === _sidebarProductId);

    if (distinct === 0) {
      if (hasExistingForProduct) {
        cta.textContent = "Remove from build";
        cta.disabled = false;
      } else {
        cta.textContent = "Add to build";
        cta.disabled = true;
      }
    } else {
      cta.textContent = `(${distinct}) Add to build — ${_formatPrice(totalCents)}`;
      cta.disabled = false;
    }
  }

  // Commit replaces all existing instances of this product with staging contents.
  // Each staged unit becomes ITS OWN record so customers can drag and rotate
  // duplicates independently on the canvas. Cart-side, Shopify merges
  // same-variant lines with identical properties into one line of qty N.
  function _commitSidebar() {
    // Length mode applies each pick immediately on row click (see
    // renderLengthSidebar) — this shared CTA button is just "done" there,
    // with nothing left to commit.
    const dialog = document.getElementById("cb-sidebar");
    if (dialog?.classList.contains("charm-builder__sidebar--length-mode")) {
      _closeSidebar();
      return;
    }

    const product = _findCharmProductById(_sidebarProductId);
    if (!product) return;

    const remaining = CharmBuilder.state.charms.filter(c => c.productId !== _sidebarProductId);

    const additions = [];
    _sidebarStaging.forEach((qty, vid) => {
      if (qty <= 0) return;
      const variant = product.variants.find(v => v.id === vid);
      if (!variant) return;
      // One record per unit. Each gets its own instanceId so positions and
      // rotations are tracked independently.
      for (let i = 0; i < qty; i++) {
        additions.push({
          ..._charmRecordFields(product, variant),
          instanceId: `charm-${Date.now()}-${Math.random().toString(36).slice(2, 7)}-${vid}-${i}`,
          x: 0,
          y: 0,
          rotation: 0,
        });
      }
    });

    CharmBuilder.setState({ charms: [...remaining, ...additions] });
    _closeSidebar();
  }

  function _closeSidebar() {
    const dialog = document.getElementById("cb-sidebar");
    if (dialog?.open) dialog.close();
  }

  // ---------------------------------------------------------------------------
  // Step 3 — review
  // ---------------------------------------------------------------------------

  function renderReview() {
    const baseEl = document.getElementById("cb-review-base");
    const charmsEl = document.getElementById("cb-review-charms");
    const totalsEl = document.getElementById("cb-review-totals");
    const errorEl = document.getElementById("cb-cart-error");

    const state = CharmBuilder.state;

    // Hide any stale cart error from a prior submit attempt on re-entry.
    if (errorEl && !errorEl.textContent) errorEl.hidden = true;

    if (baseEl) {
      baseEl.innerHTML = state.base
        ? _renderReviewRow({
            title: state.base.title,
            // Review shows product-card style rows — prefer featuredImage,
            // fall back to transparent for older persisted state.
            image: state.base.featuredImage || state.base.image,
            price: state.base.price,
            quantity: 1,
            product: _findBaseProductById(state.base.productId),
            variant: _findBaseVariant(state.base.productId, state.base.variantId),
            variantFallbackLabel: state.base.variantTitle,
          })
        : "";
    }

    if (charmsEl) {
      charmsEl.innerHTML = state.charms.length
        ? state.charms
            .map(c =>
              _renderReviewRow({
                title: c.title,
                image: c.featuredImage || c.image,
                price: c.price,
                quantity: c.quantity || 1,
                product: _findCharmProductById(c.productId),
                variant: _findCharmVariant(c.productId, c.variantId),
                variantFallbackLabel: c.variantTitle,
                // Only charm rows are removable — the base is the parent
                // product and can't be pulled out of the cart line on its own.
                instanceId: c.instanceId,
              })
            )
            .join("")
        : '<p class="charm-builder__review-empty">No charms selected.</p>';

      charmsEl.querySelectorAll('[data-action="remove-charm"]').forEach(btn => {
        btn.addEventListener("click", () => {
          CharmBuilder.removeCharm(btn.dataset.instanceId);
        });
      });
    }

    if (totalsEl) {
      const baseCents = state.base ? state.base.price : 0;
      const charmsCents = state.charms.reduce((s, c) => s + (c.price || 0) * (c.quantity || 1), 0);
      const subtotal = baseCents + charmsCents;
      // VIP discount for VIPs; otherwise the tier (set) discount — picked by charm
      // count only, but applied to the whole set, base included.
      const discount = _displayDiscount(state);
      const discountCents = discount.cents;
      const total = subtotal - discountCents;

      totalsEl.innerHTML = `
        <div class="charm-builder__review-totals-row">
          <span>Subtotal</span>
          <span>${_formatPrice(subtotal)}</span>
        </div>
        ${
          discountCents > 0
            ? `
              <div class="charm-builder__review-totals-row charm-builder__review-totals-row--discount">
                <span>${discount.label}</span>
                <span>−${_formatPrice(discountCents)}</span>
              </div>
            `
            : ""
        }
        <div class="charm-builder__review-totals-row charm-builder__review-totals-row--total">
          <span>Total</span>
          <span><span class="charm-builder__review-totals-currency">USD</span> ${_formatPrice(total)}</span>
        </div>
        <div class="charm-builder__review-totals-note">
          <p>Your Goddess Circle discount will be applied at checkout</p>
        </div>
      `;
    }
  }

  function _renderReviewRow({ title, image, price, quantity, product, variant, variantFallbackLabel, instanceId }) {
    const optionsHtml = _renderVariantOptionLines(variant, variantFallbackLabel);
    const qty = quantity || 1;
    // VIP: the struck price is the regular price (one strikethrough only)
    const vipPrice = _vipUnitPrice(product, price);
    const isVip = vipPrice !== price;
    const lineTotal = vipPrice * qty;
    const compareAt = isVip ? price : variant?.compareAtPrice;
    const showCompare = compareAt && compareAt > vipPrice;
    const compareTotal = showCompare ? compareAt * qty : 0;

    // Layout matches Shopify checkout cart-line summary (the "nested cart
    // lines" reference image): 50px thumbnail with quantity badge top-right,
    // title + variant info in middle, price aligned with title on the right.
    // instanceId is only passed for charm rows — the base row omits it, so
    // it never gets a Remove link (it's the parent product; removing it
    // would break the cart line relationship with the nested charms).
    return `
      <div class="charm-builder__review-row">
        <div class="charm-builder__review-row-image">
          <img src="${image}" alt="${title}" width="50" height="50" loading="lazy">
          <span class="charm-builder__review-row-qty-badge" aria-label="Quantity ${qty}">${qty}</span>
        </div>
        <div class="charm-builder__review-row-info">
          <p class="charm-builder__review-row-title">${title}</p>
          ${optionsHtml}
          <div class="charm-builder__review-row-price-line">
            <span class="charm-builder__review-row-price">
              ${_formatPrice(lineTotal)}
              ${showCompare ? `<span class="charm-builder__review-row-compare">${_formatPrice(compareTotal)}</span>` : ""}
            </span>
            ${
              instanceId
                ? `<button type="button" class="charm-builder__review-row-remove" data-action="remove-charm" data-instance-id="${instanceId}" aria-label="Remove ${title}">Remove</button>`
                : ""
            }
          </div>
        </div>

      </div>
    `;
  }

  function _renderVariantOptionLines(variant, fallbackLabel) {
    if (variant?.options) {
      const entries = Object.entries(variant.options).filter(([, v]) => v != null && v !== "");
      if (entries.length) {
        return entries
          .map(([k, v]) => `<p class="charm-builder__review-row-option">${k}: <span>${v}</span></p>`)
          .join("");
      }
    }
    if (fallbackLabel) {
      return `<p class="charm-builder__review-row-option">${fallbackLabel}</p>`;
    }
    return "";
  }

  function _findBaseVariant(productId, variantId) {
    const bases = window.__CHARM_BUILDER_DATA__?.bases || [];
    for (const cat of bases) {
      for (const p of cat.products || []) {
        if (p.id === productId) return p.variants.find(v => v.id === variantId);
      }
    }
    return null;
  }

  // How many charms/pendants the selected base can hold — read from its
  // variant's collectors_number_of_pendants_ metafield (a string, but always
  // 1-9 in practice). Falls back to DEFAULT_MAX_CHARMS when no base is
  // selected yet, or the metafield is blank/out of range.
  function _maxPendantsForBase() {
    const base = CharmBuilder.state.base;
    if (!base) return DEFAULT_MAX_CHARMS;
    const variant = _findBaseVariant(base.productId, base.variantId);
    const raw = parseInt(variant?.maxPendants, 10);
    return raw >= 1 && raw <= 9 ? raw : DEFAULT_MAX_CHARMS;
  }

  function _findCharmVariant(productId, variantId) {
    const product = _findCharmProductById(productId);
    return product ? product.variants.find(v => v.id === variantId) : null;
  }

  // Highest quantity of this variant a customer may add to the canvas.
  // Mirrors Shopify's own add-to-cart inventory rule so the UI stops them
  // before checkout would (Shopify rejects an add past available stock when
  // the variant is tracked with a "deny" policy — untracked variants and
  // "continue"/backorder variants have no real ceiling here).
  function _variantMaxQty(variant) {
    if (!variant) return Infinity;
    if (variant.inventoryManagement !== "shopify") return Infinity;
    if (variant.inventoryPolicy === "continue") return Infinity;
    return Math.max(0, variant.inventoryQuantity || 0);
  }

  // Total units of this exact variant already placed on the canvas.
  function _charmVariantQty(variantId) {
    return CharmBuilder.state.charms
      .filter(c => c.variantId === variantId)
      .reduce((sum, c) => sum + (c.quantity || 1), 0);
  }

  // ---------------------------------------------------------------------------
  // Progress bar
  // ---------------------------------------------------------------------------

  function updateProgressBar(state) {
    const bar = document.getElementById("cb-progress-bar");
    if (!bar) return;
    bar.hidden = false;

    const labelEl = document.getElementById("cb-step-label");
    const fillEl = document.getElementById("cb-progress-fill");
    const savingsEl = document.getElementById("cb-savings");
    const subtotalEl = document.getElementById("cb-subtotal");
    const totalEl = document.getElementById("cb-total");
    const ctaEl = document.getElementById("cb-progress-cta");

    if (labelEl) labelEl.textContent = `Step ${state.step}/3`;

    if (fillEl) {
      const fillPx = Math.round((state.step / 3) * 100);
      fillEl.style.width = fillPx + "px";
    }

    // Tier discount applies to base + charms, same rule as the review totals.
    const basePrice = state.base ? state.base.price : 0;
    const charmsPrice = state.charms.reduce((sum, c) => sum + (c.price || 0) * (c.quantity || 1), 0);
    const subtotal = basePrice + charmsPrice;
    const discountCents = _displayDiscount(state).cents; // VIP savings for VIPs, else tier discount
    const total = subtotal - discountCents;

    // Shown on Steps 1 + 2 whenever there's a price to total (i.e. a base is
    // selected). Hidden on Step 3 (Review) since the review body already
    // renders the full Subtotal/Discount/Total breakdown inline — showing
    // this compact version too would duplicate it in the footer.
    const showTotals = state.step !== 3 && subtotal > 0;
    // Total only earns its own line once a charm discount actually changes
    // it — otherwise it's identical to Subtotal and would just repeat it.
    const showTotal = showTotals && discountCents > 0;

    if (savingsEl) {
      savingsEl.hidden = !showTotal;
      // Two spans (label/value), same pattern as #cb-subtotal and #cb-total,
      // so all three can be laid out identically with display:flex.
      savingsEl.innerHTML = showTotal ? `<span>Savings:</span><span>-${_formatPrice(discountCents)}</span>` : "";
    }

    if (subtotalEl) {
      subtotalEl.hidden = !showTotals;
      if (showTotals) {
        const itemCount = 1 + state.charms.reduce((sum, c) => sum + (c.quantity || 1), 0);
        subtotalEl.innerHTML = `<span>Subtotal (${itemCount} item${itemCount !== 1 ? "s" : ""}):</span><span>${_formatPrice(subtotal)}</span>`;
      } else {
        subtotalEl.innerHTML = "";
      }
    }

    if (totalEl) {
      totalEl.hidden = !showTotal;
      totalEl.innerHTML = showTotal ? `<span>Total:</span><span>${_formatPrice(total)}</span>` : "";
    }

    if (ctaEl) {
      if (state.step === 1) {
        ctaEl.textContent = "Add Charms"; // leads into the charms step (Figma copy update)
        ctaEl.disabled = !state.base;
        ctaEl.onclick = () => {
          if (state.base) CharmBuilder.setState({ step: 2 });
        };
      } else if (state.step === 2) {
        ctaEl.textContent = state.charms.length === 0 ? "Choose a Charm" : "Review";
        ctaEl.disabled = state.charms.length === 0;
        ctaEl.onclick = () => {
          if (state.charms.length > 0) CharmBuilder.setState({ step: 3 });
        };
      } else if (state.step === 3) {
        ctaEl.textContent = "Add to Cart";
        ctaEl.disabled = !state.base;
        ctaEl.onclick = () => CharmBuilderCart.submit();
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Canvas
  // ---------------------------------------------------------------------------

  function updateCanvas(state) {
    _renderCanvasBase(state);
    _renderCanvasCharms(state);
  }

  function _renderCanvasBase(state) {
    const el = document.getElementById("cb-base-image");
    if (!el) return;

    if (!state.base) {
      el.innerHTML = "";
      delete el.dataset.closedImage;
      delete el.dataset.openImage;
      return;
    }

    const openImage = state.base.openImage || "";
    if (el.dataset.closedImage === state.base.image && el.dataset.openImage === openImage) return;

    el.dataset.closedImage = state.base.image;
    el.dataset.openImage = openImage;

    // "Open slots" overlay — a decorative second image (any of png/jpg/gif)
    // crossfaded in via .is-showing-open-slots while a charm is being
    // dragged/rotated on the canvas. See charm-builder-drag.js. Omitted
    // entirely when the base variant has no charm_builder_open_image
    // metafield, so unconfigured bases render exactly as before.
    const openImageMarkup = openImage
      ? `<img
          class="charm-builder__base-image-open"
          src="${openImage}"
          alt=""
          aria-hidden="true"
          width="800"
          height="800"
        >`
      : "";

    el.innerHTML = `<img
      class="charm-builder__base-image-closed"
      src="${state.base.image}"
      alt="${state.base.title}"
      width="800"
      height="800"
    >${openImageMarkup}`;
  }

  // Inline SVG markup for the canvas-charm controls, read once from the
  // #cb-charm-control-icons <template> (charm-builder-canvas.liquid). Cached
  // on the function itself — init renders before a module-level `let` here
  // would be initialised (TDZ).
  function _charmControlIcons() {
    if (_charmControlIcons.cache) return _charmControlIcons.cache;
    const tpl = document.getElementById("cb-charm-control-icons");
    const read = name => tpl?.content.querySelector(`[data-icon="${name}"]`)?.innerHTML.trim() || "";
    _charmControlIcons.cache = { rotate: read("rotate"), drag: read("drag"), close: read("close"), caret: read("caret") };
    return _charmControlIcons.cache;
  }

  function _renderCanvasCharms(state) {
    const dropZone = document.getElementById("cb-canvas-drop-zone");
    if (!dropZone) return;

    // Tear down any existing Draggable instances before we rebuild the DOM.
    if (window.CharmBuilderDrag) window.CharmBuilderDrag.detachAll();

    // No base → nothing should sit on the canvas yet.
    if (!state.base || !state.charms.length) {
      if (dropZone.innerHTML) dropZone.innerHTML = "";
      return;
    }

    // Phase B: one overlay per charm record. Quantity > 1 is shown as a ×N
    // badge in the corner rather than separate units, so each drag updates a
    // single x/y pair in state.
    // Each charm record is one visual unit — duplicates of the same variant
    // render as separate draggable overlays so the customer can position
    // and rotate each one independently.
    const total = state.charms.length;
    const icons = _charmControlIcons();
    // "Drag to rearrange" hint — shown once ever, on the very first charm
    // only (Figma 8214-56004). Dismissed by CharmBuilderDrag on first drag.
    const showDragHint = !(window.CharmBuilderDrag && window.CharmBuilderDrag.isHintSeen());
    const html = state.charms
      .map((charm, index) => {
        const pos = _charmCanvasPosition(charm, index, total);
        const safeTitle = (charm.title || "").replace(/"/g, "&quot;");
        const rotation = typeof charm.rotation === "number" ? charm.rotation : 0;
        const hint =
          showDragHint && index === 0
            ? `<span class="charm-builder__canvas-charm-hint" aria-hidden="true">${icons.caret}<span>Drag to rearrange</span></span>`
            : "";
        // Controls row under the charm (Figma 8210-56003): rotate | drag | close.
        // Rotate + remove are real <button>s so GSAP's `dragClickables: false`
        // on the translation Draggable skips drag-init when the press lands on
        // them; the rotation Draggable uses the rotate button as its `trigger`.
        // The drag handle is a plain <span>, so pressing it starts translation
        // like pressing the charm itself. The row sits OUTSIDE the rotor so it
        // stays upright when the charm rotates. Remove click removes this
        // charm record from state (and from the review screen).
        const controls = `
          <div class="charm-builder__canvas-charm-controls">
            <button type="button" class="charm-builder__canvas-charm-rotate" aria-label="Drag to rotate ${safeTitle}">${icons.rotate}</button>
            <span class="charm-builder__canvas-charm-drag" aria-hidden="true">${icons.drag}${hint}</span>
            <button type="button" class="charm-builder__canvas-charm-remove" aria-label="Remove ${safeTitle} from canvas">${icons.close}</button>
          </div>`;
        // Two Draggable instances can't share one element, so the rotation
        // Draggable targets the inner `.rotor` div while translation stays
        // on the outer wrapper.
        return `<div
          class="charm-builder__canvas-charm${hint ? " has-drag-hint" : ""}"
          data-instance-id="${charm.instanceId}"
          data-unit-index="0"
          data-pos-left="${pos.left}"
          data-pos-top="${pos.top}"
          data-rotation="${rotation}"
          data-image-width="${charm.imageWidth || 0}"
          data-image-height="${charm.imageHeight || 0}"
          style="left: ${pos.left}%; top: ${pos.top}%;"
        >
          <div class="charm-builder__canvas-charm-rotor">
            <img src="${charm.image}" alt="${safeTitle}" ${index === 0 ? 'loading="eager" fetchpriority="high"' : 'loading="lazy"'} draggable="false">
          </div>
          ${controls}
        </div>`;
      })
      .join("");

    dropZone.innerHTML = html;

    // Wire remove buttons. Stop pointerdown propagation so GSAP Draggable
    // (which listens on the wrapper) never sees the press — otherwise even
    // with dragClickables:false the gesture pipeline can race the click.
    // Click handler removes this charm record from state, which also removes
    // it from the review screen via state.charms.
    dropZone.querySelectorAll(".charm-builder__canvas-charm-remove").forEach(btn => {
      ["pointerdown", "mousedown", "touchstart"].forEach(evt => {
        btn.addEventListener(evt, e => e.stopPropagation());
      });
      btn.addEventListener("click", e => {
        e.stopPropagation();
        const wrapper = btn.closest(".charm-builder__canvas-charm");
        const instanceId = wrapper?.dataset.instanceId;
        if (instanceId) CharmBuilder.removeCharm(instanceId);
      });
    });

    // Attach Draggable to each freshly rendered overlay. attach() is a no-op
    // if GSAP / Draggable haven't loaded yet — the overlays remain static.
    if (window.CharmBuilderDrag) {
      state.charms.forEach(charm => {
        const el = dropZone.querySelector(`.charm-builder__canvas-charm[data-instance-id="${charm.instanceId}"]`);
        if (el) window.CharmBuilderDrag.attach(el, charm.instanceId);
      });
    }

    // Defer sizing to the next animation frame so the browser completes
    // any layout reflow triggered by innerHTML + GSAP before we read offsetWidth.
    requestAnimationFrame(() => {
      dropZone.querySelectorAll(".charm-builder__canvas-charm").forEach(_applyCharmSize);
    });
  }

  // ---------------------------------------------------------------------------
  // Canvas charm sizing
  // ---------------------------------------------------------------------------

  // Responsive sizing: charms render at scale = canvasWidth / 800.
  // At desktop (800 px canvas) → 100 % of uploaded dimensions.
  // At mobile  (~400 px canvas) → 50 % of uploaded dimensions.
  // This mirrors how the base necklace image scales, so charm proportions
  // always match the photo regardless of viewport width.
  const _CANVAS_REFERENCE_W = 800;

  // Read scale from the rendered base-necklace img, not its container.
  // The img is constrained by max-width: 800px on desktop and width: 100% on
  // mobile, so its offsetWidth is the true reference frame for charm sizing.
  function _getCanvasScale() {
    const img = document.querySelector("#cb-base-image img");
    if (img && img.offsetWidth > 0) return img.offsetWidth / _CANVAS_REFERENCE_W;
    // Fallback before base image is selected
    const el = document.getElementById("cb-base-image");
    if (el && el.offsetWidth > 0) return el.offsetWidth / _CANVAS_REFERENCE_W;
    return 0.5;
  }

  function _applyCharmSize(el) {
    const srcW = parseInt(el.dataset.imageWidth, 10);
    const srcH = parseInt(el.dataset.imageHeight, 10);
    if (srcW && srcH) {
      // Fast path: use Liquid-serialised upload dimensions (always available).
      const scale = _getCanvasScale();
      el.style.setProperty("--cb-charm-w", Math.round(srcW * scale) + "px");
      el.style.setProperty("--cb-charm-h", Math.round(srcH * scale) + "px");
      return;
    }
    // Fallback: derive from the charm img's natural size (e.g. restored from
    // localStorage with missing imageWidth). Re-read scale at apply time so
    // it reflects the layout at the moment the image fires, not when this
    // function was called.
    const img = el.querySelector("img");
    if (!img) return;
    const apply = () => {
      if (!img.naturalWidth) return;
      const scale = _getCanvasScale();
      el.style.setProperty("--cb-charm-w", Math.round(img.naturalWidth * scale) + "px");
      el.style.setProperty("--cb-charm-h", Math.round(img.naturalHeight * scale) + "px");
    };
    if (img.complete && img.naturalWidth) apply();
    else img.addEventListener("load", apply, { once: true });
  }

  function _reapplyAllCharmSizes() {
    document.querySelectorAll(".charm-builder__canvas-charm").forEach(_applyCharmSize);
  }

  // Watch the canvas element (not the drop-zone) for genuine layout changes
  // (viewport resize, orientation change). Observing the drop-zone would fire
  // on every charm add/remove, causing incorrect re-sizes mid-render.
  // RAF defers the re-size pass until after the browser has completed layout
  // for the current resize tick, ensuring offsetWidth reads are accurate.
  (function _initCharmSizeWatcher() {
    const canvas = document.getElementById("cb-canvas");
    if (!canvas || !window.ResizeObserver) return;
    new ResizeObserver(() => requestAnimationFrame(_reapplyAllCharmSizes)).observe(canvas);
  })();

  /**
   * Returns { left, top } as percentages of the canvas inner.
   * - Honours an explicit charm.x / charm.y if present (Phase B drag will set these).
   * - Otherwise auto-lays out along a gentle horizontal arc beneath the base,
   *   spread between 18% and 82% horizontally with a small downward dip at center.
   */
  function _charmCanvasPosition(charm, index, total) {
    // Use a saved drag position only when the charm has actually been moved —
    // (0, 0) is the default for a newly added charm and should fall through
    // to auto-layout. The OR guard also covers the edge case of x=0 after a
    // drag to the exact left edge (extremely unlikely given GSAP bounds padding).
    if (charm.x || charm.y) {
      return { left: charm.x, top: charm.y };
    }
    if (total === 1) {
      return { left: 50, top: 72 };
    }
    const t = index / (total - 1); // 0..1
    const left = 18 + t * 64; // 18% → 82%
    const arcDip = Math.sin(t * Math.PI) * 6; // 0 at edges, 6% at center
    const top = 66 + arcDip; // 66% → 72%
    return { left, top };
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function _variantForMetal(variants, metal) {
    if (!metal) return null;
    return variants.find(v => v.options?.Material === metal) || null;
  }

  function _findCharmByProductId(productId) {
    // Returns the most recently added charm instance for a productId, or null.
    const charms = CharmBuilder.state.charms;
    for (let i = charms.length - 1; i >= 0; i--) {
      if (charms[i].productId === productId) return charms[i];
    }
    return null;
  }

  function _findCharmProductById(productId) {
    // Lookup a product across all charm categories in the serialised data.
    const charms = window.__CHARM_BUILDER_DATA__?.charms || [];
    for (const cat of charms) {
      for (const p of cat.products || []) {
        if (p.id === productId) return p;
      }
    }
    return null;
  }

  // Charm product holding this variant — restore links (?cb_charms=) carry
  // only variant ids.
  function _findCharmProductByVariantId(variantId) {
    const charms = window.__CHARM_BUILDER_DATA__?.charms || [];
    for (const cat of charms) {
      for (const p of cat.products || []) {
        if ((p.variants || []).some(v => v.id === variantId)) return p;
      }
    }
    return null;
  }

  // Product/variant fields every charm record carries. Shared by Select
  // Style, the sidebar and restore links so all three build identical
  // records; callers add instanceId / x / y / rotation as needed.
  function _charmRecordFields(product, variant) {
    return {
      productId: product.id,
      variantId: variant.id,
      title: product.title,
      variantTitle: variant.title,
      image: variant.image, // transparent — canvas
      featuredImage: variant.featuredImage || variant.image, // standard — review/cart
      price: variant.price,
      quantity: 1,
      imageWidth: variant.imageWidth || 0,
      imageHeight: variant.imageHeight || 0,
    };
  }

  // Same shape as _findBaseProductById, keyed by variant — the PDP CTA's
  // ?cb_base= carries only a variant id.
  function _findBaseProductByVariantId(variantId) {
    const bases = window.__CHARM_BUILDER_DATA__?.bases || [];
    for (const cat of bases) {
      for (const p of cat.products || []) {
        if ((p.variants || []).some(v => v.id === variantId)) return p;
      }
    }
    return null;
  }

  function _findBaseProductById(productId) {
    // Lookup a product across all base categories in the serialised data.
    const bases = window.__CHARM_BUILDER_DATA__?.bases || [];
    for (const cat of bases) {
      for (const p of cat.products || []) {
        if (p.id === productId) return p;
      }
    }
    return null;
  }

  // Not every base has a length option — chains do, most single necklaces
  // don't. Returns the option key name if one of this product's variants has
  // a length-like option (see LENGTH_OPTION_ALIASES), else null.
  function _lengthOptionKey(product) {
    const keys = new Set();
    (product.variants || []).forEach(v => Object.keys(v.options || {}).forEach(k => keys.add(k)));
    for (const k of keys) {
      if (LENGTH_OPTION_ALIASES.includes(k.toLowerCase())) return k;
    }
    return null;
  }

  // Resolves which variant should apply when a base product gets selected
  // (grid click, or the initial default-base pick at page load). If the
  // customer previously chose a length for this exact product, that wins;
  // otherwise falls back to the first available length for the current
  // material, then to a plain material-only match for lengthless bases.
  function _resolveBaseVariant(product, activeMetal) {
    const lengthKey = _lengthOptionKey(product);
    if (lengthKey) {
      const preferred = _baseLengthPrefs.get(product.id);
      if (preferred != null) {
        const match = product.variants.find(
          v =>
            (!activeMetal || v.options?.Material === activeMetal) &&
            v.options?.[lengthKey] === preferred &&
            v.available !== false
        );
        if (match) return match;
      }
      const firstForMaterial = product.variants.find(
        v => (!activeMetal || v.options?.Material === activeMetal) && v.available !== false
      );
      if (firstForMaterial) return firstForMaterial;
    }
    return _variantForMetal(product.variants, activeMetal) || product.variants[0];
  }

  function _variantOptionLabel(variant) {
    // The display label is whichever option value isn't Material — usually a style/size.
    // Fallback to variant.title if nothing distinct.
    if (!variant.options) return null;
    const keys = Object.keys(variant.options).filter(k => k !== "Material");
    if (!keys.length) return null;
    return keys.map(k => variant.options[k]).join(" / ");
  }

  // Inline +/- stepper on single-variant cards. Each click adds or removes
  // ONE separate record (so the customer can see and rotate each unit on the
  // canvas independently). When the last record for this product is removed,
  // the card returns to "Select Style" state.
  function _changeCharmQty(productId, variantId, delta) {
    const existing = CharmBuilder.state.charms.filter(c => c.productId === productId);
    if (delta < 0) {
      // Remove the most recently added record for this product.
      if (!existing.length) return;
      const last = existing[existing.length - 1];
      CharmBuilder.removeCharm(last.instanceId);
      return;
    }
    // delta > 0 — add another record cloned from the first existing one.
    if (!existing.length) return;
    const template = existing[0];
    const variant =
      _findCharmVariant(productId, variantId) || _findCharmVariant(template.productId, template.variantId);
    // Mirrors Shopify's own inventory rule — the "+" button is already
    // disabled at this point (see renderCharmGrid), but guard here too since
    // this is the actual mutation point.
    if (_charmVariantQty(variant?.id ?? template.variantId) >= _variantMaxQty(variant)) return;
    const charms = [
      ...CharmBuilder.state.charms,
      {
        ...template,
        instanceId: `charm-${Date.now()}-${Math.random().toString(36).slice(2, 7)}-${template.variantId}`,
        quantity: 1,
        x: 0,
        y: 0,
        rotation: 0,
        image: variant?.image || template.image,
        featuredImage: variant?.featuredImage || template.featuredImage,
      },
    ];
    CharmBuilder.setState({ charms });
  }

  function _swatchFallback(material) {
    if (/gold vermeil/i.test(material) || /14k/i.test(material))
      return "radial-gradient(circle at 40% 40%, rgba(252,241,213,1) 0%, rgba(234,213,168,1) 100%)";
    if (/rose\s*gold/i.test(material)) return "radial-gradient(circle at 40% 40%, #f4c0a5 0%, #c97b5a 100%)";
    if (/silver/i.test(material))
      return "radial-gradient(circle at 40% 40%, rgba(248,248,247,0.8) 0%, rgba(193,189,187,0.8) 100%), linear-gradient(90deg, rgb(224,220,217) 0%, rgb(224,220,217) 100%)";
    return "#ccc";
  }

  function _formatPrice(cents) {
    const dollars = cents / 100;
    return "$" + (dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2));
  }

  // ---------------------------------------------------------------------------
  // VIP sale pricing (holiday gate) — DISPLAY ONLY
  // ---------------------------------------------------------------------------
  // For VIP visitors (window.AweHolidayGate from assets/awe-holiday-gate.js),
  // every base and charm shows the VIP price unless the product carries one of
  // the visitor's excluded tags (vipExcludeTags, from Liquid) or is a gift card.
  // Checkout applies the real discount via the _campaign cart properties /
  // attributes; this only mirrors it. Non-VIPs see the builder unchanged. While
  // the VIP discount shows, the set (tier) discount isn't shown — they don't stack.

  function _vipGate() {
    const gate = window.AweHolidayGate;
    return gate && gate.discount > 0 && typeof gate.applyToCents === "function" ? gate : null;
  }

  function _isVipExcluded(product) {
    const gate = _vipGate();
    if (!gate || !product) return true;
    const excluded = gate.excludedTags || [];
    return (product.vipExcludeTags || []).some(tag => tag === "__gift_card__" || excluded.includes(tag));
  }

  /** Unit price the visitor sees — VIP price when it applies, else the normal price. */
  function _vipUnitPrice(product, cents) {
    if (!cents || _isVipExcluded(product)) return cents;
    return _vipGate().applyToCents(cents);
  }

  function _productForId(productId) {
    return _findBaseProductById(productId) || _findCharmProductById(productId);
  }

  /** VIP savings on the current set (base + charms), in cents. 0 when not VIP. */
  function _vipSavingsCents(state) {
    if (!_vipGate()) return 0;
    const line = (productId, cents, qty) => (cents - _vipUnitPrice(_productForId(productId), cents)) * qty;
    const baseSavings = state.base ? line(state.base.productId, state.base.price || 0, 1) : 0;
    return state.charms.reduce((sum, c) => sum + line(c.productId, c.price || 0, c.quantity || 1), baseSavings);
  }

  /** The discount shown on totals: VIP savings for VIPs, otherwise the set (tier) discount. */
  function _displayDiscount(state) {
    if (_vipGate()) {
      const cents = _vipSavingsCents(state);
      return { cents, label: `VIP discount (${_vipGate().discount}% off)` };
    }
    const pct = CharmBuilder.getDiscountPercent();
    return { cents: CharmBuilder.getDiscountCents(), label: pct > 0 ? `Set discount (${pct}% off)` : "" };
  }

  /** Card price: VIP → struck original + VIP price + badge; otherwise the plain price. */
  function _cardPriceHtml(product, cents) {
    const vip = _vipUnitPrice(product, cents);
    if (vip === cents) return _formatPrice(cents);
    return `<span class="visually-hidden">Original price </span><s class="charm-builder__price-original">${_formatPrice(cents)}</s> `
      + `<span class="visually-hidden">VIP price </span><span class="charm-builder__price-vip">${_formatPrice(vip)}</span> `
      + `<span class="charm-builder__price-vip-badge">${_vipGate().discount}% off</span>`;
  }

  // Scoped variant — extracts materials from a single products array only
  // (e.g. one category's products), used to limit the swatch bar to what the
  // active tab actually offers. Out-of-stock variants are excluded — a
  // material shouldn't be selectable if picking it just lands on "No bases/
  // charms available in this material" because everything in that material
  // happens to be sold out.
  function _extractMaterialsFromProducts(products) {
    const seen = new Set();
    (products || []).forEach(({ variants }) => {
      (variants || []).forEach(({ options, available }) => {
        if (options?.Material && available !== false) seen.add(options.Material);
      });
    });
    const arr = [...seen];
    const goldIdx = arr.findIndex(m => /gold vermeil/i.test(m));
    if (goldIdx > 0) arr.unshift(arr.splice(goldIdx, 1)[0]);
    return arr;
  }

  function _extractMaterials() {
    const bases = (window.__CHARM_BUILDER_DATA__?.bases || []).flatMap(cat => cat.products || []);
    const charms = (window.__CHARM_BUILDER_DATA__?.charms || []).flatMap(cat => cat.products || []);
    return _extractMaterialsFromProducts([...bases, ...charms]);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", _charmBuilderInit);
} else {
  _charmBuilderInit();
}
